import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import { useState, type ComponentType, type ReactNode } from 'react';
import {
  certificaattypeLijst,
  functietitelLijst,
  talentpoolLijst,
  useLocatieMutaties,
  useLocaties,
  useReferentielijst,
  useReferentieMutaties,
  vaardigheidLijst,
  type Referentielijst,
} from '../../api/referentie.ts';
import type { Locatie } from '../../api/typen.ts';
import { useSessie } from '../../auth/SessieContext.tsx';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { NieuweLocatieDialoog } from '../../componenten/keuzevelden.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { Zoekveld } from '../../componenten/Zoekveld.tsx';
import { ofStreepje } from '../../helpers/formatteer.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import {
  CertificaattypeFormulier,
  FunctietitelFormulier,
  LocatieFormulier,
  TalentpoolFormulier,
  VaardigheidFormulier,
  type FormulierProps,
} from './ReferentieFormulieren.tsx';

interface Kolom<T> {
  label: string;
  waarde: (item: T) => ReactNode;
}

interface TabelProps<T extends { id: string; naam: string }, I> {
  lijst: Referentielijst<T, I>;
  kolommen: Kolom<T>[];
  Formulier: ComponentType<FormulierProps<T>>;
}

function Referentietabel<T extends { id: string; naam: string }, I>({ lijst, kolommen, Formulier }: TabelProps<T, I>) {
  const { isBeheerder } = useSessie();
  const { meld, meldFout } = useMeldingen();
  const [zoek, setZoek] = useState('');
  const query = useReferentielijst(lijst, zoek);
  const mutaties = useReferentieMutaties(lijst);
  const [formulier, setFormulier] = useState<{ open: boolean; bestaand?: T }>({ open: false });
  const [verwijderen, setVerwijderen] = useState<T | null>(null);

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', mb: 2 }}>
        <Zoekveld waarde={zoek} onChange={setZoek} placeholder={`Zoek ${lijst.meervoud}`} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormulier({ open: true })}>
          Nieuwe {lijst.enkelvoud}
        </Button>
      </Stack>
      <Laadstatus query={query} leeg={`Geen ${lijst.meervoud} gevonden.`}>
        {(items) => (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {kolommen.map((kolom) => (
                    <TableCell key={kolom.label}>{kolom.label}</TableCell>
                  ))}
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    {kolommen.map((kolom) => (
                      <TableCell key={kolom.label}>{kolom.waarde(item)}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        aria-label="Bewerken"
                        onClick={() => setFormulier({ open: true, bestaand: item })}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {isBeheerder && (
                        <IconButton size="small" aria-label="Verwijderen" onClick={() => setVerwijderen(item)}>
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Laadstatus>
      <Formulier open={formulier.open} onSluit={() => setFormulier({ open: false })} bestaand={formulier.bestaand} />
      <BevestigDialoog
        open={verwijderen !== null}
        titel={`${lijst.enkelvoud.charAt(0).toUpperCase()}${lijst.enkelvoud.slice(1)} verwijderen`}
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijder.isPending}
        onSluit={() => setVerwijderen(null)}
        onBevestig={() => {
          if (verwijderen) {
            mutaties.verwijder
              .mutateAsync(verwijderen.id)
              .then(() => {
                meld('Verwijderd');
                setVerwijderen(null);
              })
              .catch(meldFout);
          }
        }}
      >
        {verwijderen?.naam} wordt verwijderd. Dit kan alleen zolang niets ernaar verwijst.
      </BevestigDialoog>
    </>
  );
}

function Synoniemen({ synoniemen }: { synoniemen: readonly string[] }) {
  return synoniemen.length === 0 ? (
    <>–</>
  ) : (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {synoniemen.map((synoniem) => (
        <Chip key={synoniem} label={synoniem} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

function LocatiesTab() {
  const { isBeheerder } = useSessie();
  const { meld, meldFout } = useMeldingen();
  const [zoek, setZoek] = useState('');
  const locaties = useLocaties(zoek);
  const mutaties = useLocatieMutaties();
  const [nieuw, setNieuw] = useState(false);
  const [bewerken, setBewerken] = useState<Locatie | null>(null);
  const [verwijderen, setVerwijderen] = useState<Locatie | null>(null);

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', mb: 2 }}>
        <Zoekveld waarde={zoek} onChange={setZoek} placeholder="Zoek op plaats of postcode" />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNieuw(true)}>
          Nieuwe locatie
        </Button>
      </Stack>
      <Laadstatus query={locaties} leeg="Geen locaties gevonden.">
        {(items) => (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Postcode</TableCell>
                  <TableCell>Plaats</TableCell>
                  <TableCell>Provincie</TableCell>
                  <TableCell>Coördinaten</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((locatie) => (
                  <TableRow key={locatie.id} hover>
                    <TableCell>{locatie.postcode}</TableCell>
                    <TableCell>{locatie.plaats}</TableCell>
                    <TableCell>{ofStreepje(locatie.provincie)}</TableCell>
                    <TableCell>
                      {locatie.lat !== null && locatie.lon !== null ? `${locatie.lat}, ${locatie.lon}` : '–'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" aria-label="Bewerken" onClick={() => setBewerken(locatie)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {isBeheerder && (
                        <IconButton size="small" aria-label="Verwijderen" onClick={() => setVerwijderen(locatie)}>
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Laadstatus>
      <NieuweLocatieDialoog open={nieuw} onSluit={() => setNieuw(false)} onGemaakt={() => undefined} />
      {bewerken && <LocatieFormulier open onSluit={() => setBewerken(null)} bestaand={bewerken} />}
      <BevestigDialoog
        open={verwijderen !== null}
        titel="Locatie verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijder.isPending}
        onSluit={() => setVerwijderen(null)}
        onBevestig={() => {
          if (verwijderen) {
            mutaties.verwijder
              .mutateAsync(verwijderen.id)
              .then(() => {
                meld('Locatie verwijderd');
                setVerwijderen(null);
              })
              .catch(meldFout);
          }
        }}
      >
        {verwijderen?.plaats} ({verwijderen?.postcode}) wordt verwijderd. Dit kan alleen als de locatie nergens meer
        gebruikt wordt.
      </BevestigDialoog>
    </>
  );
}

const tabbladen = ['talentpools', 'functietitels', 'vaardigheden', 'certificaattypes', 'locaties'] as const;
type Tabblad = (typeof tabbladen)[number];

/**
 * De referentielijsten: talentpools, functietitels, vaardigheden, certificaattypes en locaties.
 * @returns De pagina.
 */
export function ReferentiePagina() {
  const { krijg, zet } = useZoekparameters();
  const tab = (tabbladen as readonly string[]).includes(krijg('lijst')) ? (krijg('lijst') as Tabblad) : 'talentpools';

  return (
    <Pagina
      titel="Referentielijsten"
      ondertitel="De gedeelde lijsten waarmee kandidaten, cv's en vacatures worden ingedeeld en vergeleken."
    >
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_gebeurtenis, waarde: Tabblad) => zet({ lijst: waarde === 'talentpools' ? undefined : waarde })}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab value="talentpools" label="Talentpools" />
          <Tab value="functietitels" label="Functietitels" />
          <Tab value="vaardigheden" label="Vaardigheden" />
          <Tab value="certificaattypes" label="Certificaattypes" />
          <Tab value="locaties" label="Locaties" />
        </Tabs>
      </Paper>
      <Paper sx={{ p: 2 }}>
        {tab === 'talentpools' && (
          <Referentietabel
            lijst={talentpoolLijst}
            Formulier={TalentpoolFormulier}
            kolommen={[
              { label: 'Naam', waarde: (item) => item.naam },
              { label: 'Omschrijving', waarde: (item) => ofStreepje(item.omschrijving) },
            ]}
          />
        )}
        {tab === 'functietitels' && (
          <Referentietabel
            lijst={functietitelLijst}
            Formulier={FunctietitelFormulier}
            kolommen={[
              { label: 'Naam', waarde: (item) => item.naam },
              { label: 'Synoniemen', waarde: (item) => <Synoniemen synoniemen={item.synoniemen} /> },
            ]}
          />
        )}
        {tab === 'vaardigheden' && (
          <Referentietabel
            lijst={vaardigheidLijst}
            Formulier={VaardigheidFormulier}
            kolommen={[
              { label: 'Naam', waarde: (item) => item.naam },
              { label: 'Synoniemen', waarde: (item) => <Synoniemen synoniemen={item.synoniemen} /> },
            ]}
          />
        )}
        {tab === 'certificaattypes' && (
          <Referentietabel
            lijst={certificaattypeLijst}
            Formulier={CertificaattypeFormulier}
            kolommen={[
              { label: 'Naam', waarde: (item) => item.naam },
              { label: 'Uitgever', waarde: (item) => ofStreepje(item.uitgever) },
              {
                label: 'Geldigheid',
                waarde: (item) => (item.geldigheidMaanden ? `${item.geldigheidMaanden} maanden` : 'Onbeperkt'),
              },
            ]}
          />
        )}
        {tab === 'locaties' && <LocatiesTab />}
      </Paper>
    </Pagina>
  );
}
